/**
 * API Error Messages Configuration
 *
 * Maps error codes to user-friendly messages.
 * Different services may use the same error code with different meanings.
 */

import type { ServiceName } from './api';

/**
 * Error message map type
 * Maps error code to user-friendly message
 */
export type ErrorMessageMap = Record<number, string>;

/**
 * Service-specific error messages
 * Each service can have its own error code mappings
 * TODO: use message key instead of hardcoded strings
 */
export const SERVICE_ERROR_MESSAGES: Record<ServiceName, ErrorMessageMap> = {
  bff: {
    // Authentication errors
    1001: 'Authentication failed. Please log in again.',
    1002: 'Session expired. Please log in again.',
    1003: 'Invalid credentials.',

    // Authorization errors
    2001: 'You do not have permission to perform this action.',
    2002: 'Access denied.',

    // Validation errors
    3001: 'Invalid request parameters.',
    3002: 'Missing required fields.',
    3003: 'Invalid data format.',

    // Resource errors
    4001: 'Resource not found.',
    4002: 'Resource already exists.',

    // Rate limiting
    5001: 'Too many requests. Please try again later.',

    // Server errors
    9999: 'An unexpected error occurred. Please try again.',
  },

  oex: {
    // Authentication errors
    1001: 'Trading authentication failed.',
    1002: 'Trading session expired.',

    // Order errors
    3001: 'Invalid order parameters.',
    3002: 'Insufficient balance.',
    3003: 'Order size too small.',
    3004: 'Order size too large.',
    3005: 'Price out of range.',

    // Market errors
    4001: 'Market not found.',
    4002: 'Market is closed.',
    4003: 'Trading is suspended.',

    // Rate limiting
    5001: 'Order rate limit exceeded.',

    // Server errors
    9999: 'Trading service error. Please try again.',
  },

  nts: {
    // Authentication errors
    1001: 'Notification service authentication failed.',

    // Subscription errors
    3001: 'Invalid subscription parameters.',
    3002: 'Subscription limit reached.',

    // Server errors
    9999: 'Notification service error. Please try again.',
  },

  be: {
    // Authentication errors
    1001: 'Backend authentication failed.',
    1002: 'Backend session expired.',

    // Validation errors
    3001: 'Invalid request parameters.',
    3002: 'Missing required fields.',

    // Server errors
    9999: 'Backend service error. Please try again.',
  },

  worker: {
    // Authentication errors
    1001: 'Worker authentication failed.',
    1002: 'Worker session expired.',

    // Server errors
    9999: 'Worker service error. Please try again.',
  },
};

/**
 * Default error messages for common codes
 * Used as fallback when service-specific message is not available
 */
export const DEFAULT_ERROR_MESSAGES: ErrorMessageMap = {
  // Authentication
  1001: 'Authentication failed.',
  1002: 'Session expired.',
  1003: 'Invalid credentials.',

  // Authorization
  2001: 'Permission denied.',
  2002: 'Access denied.',

  // Validation
  3001: 'Invalid request.',
  3002: 'Missing required data.',
  3003: 'Invalid data format.',

  // Resource
  4001: 'Not found.',
  4002: 'Already exists.',

  // Rate limiting
  5001: 'Too many requests.',

  // Server
  9999: 'An error occurred.',
};

/**
 * Get user-friendly error message
 *
 * @param service - Service name
 * @param code - Error code
 * @param fallbackMessage - Optional fallback message from API response
 * @returns User-friendly error message
 *
 * @example
 * const message = getErrorMessage('bff', 1001);
 * // => 'Authentication failed. Please log in again.'
 *
 * @example
 * const message = getErrorMessage('oex', 3002);
 * // => 'Insufficient balance.'
 *
 * @example
 * const message = getErrorMessage('bff', 9999, 'Custom error from API');
 * // => 'An unexpected error occurred. Please try again.'
 */
export function getErrorMessage(
  service: ServiceName,
  code: number,
  fallbackMessage?: string,
): string {
  // Try service-specific message first
  const serviceMessage = SERVICE_ERROR_MESSAGES[service]?.[code];
  if (serviceMessage) {
    return serviceMessage;
  }

  // Try default message
  const defaultMessage = DEFAULT_ERROR_MESSAGES[code];
  if (defaultMessage) {
    return defaultMessage;
  }

  // Use fallback message from API response
  if (fallbackMessage) {
    return fallbackMessage;
  }

  // Last resort
  return `Error ${code}: Request failed`;
}
